import { useAuthCardContext } from "@/features/auth/context/AuthCardContext";
import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from "react";

type ButtonVariant = "primary" | "secondary";

type ButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    iconLeft?: ReactNode;
    variant?: ButtonVariant;
    fullWidth?: boolean;
  }
>;

export function Button({
  children,
  className = "",
  disabled = false,
  fullWidth = true,
  iconLeft,
  type = "button",
  variant = "primary",
  ...props
}: ButtonProps) {
  const { buttonSize } = useAuthCardContext();

  const baseClassName = [
    buttonSize === "compact"
      ? "inline-flex min-h-9 items-center justify-center gap-2 rounded-sm px-5 py-1.5 text-xs"
      : "inline-flex min-h-12 items-center justify-center gap-2 rounded-sm px-5 py-3 text-body-2",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150",
    "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ].join(" ");

  const variantClassName =
    variant === "secondary"
      ? [
          "border border-text-secondary bg-surface-card",
          "text-text-secondary",
          "hover:border-brand-primary hover:bg-surface-accent-soft",
        ].join(" ")
      : [
          "bg-brand-primary text-text-inverse",
          "hover:bg-brand-primary-hover active:bg-brand-primary-active",
        ].join(" ");

  const buttonClassName = [
    baseClassName,
    variantClassName,
    fullWidth ? "w-full" : "",
    disabled
      ? "!border-[#e5e7eb] !bg-[#f1f1f3] !text-[#8c939b] !shadow-none cursor-not-allowed opacity-100"
      : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={buttonClassName} type={type} {...props}>
      {iconLeft ? (
        <span className="inline-flex size-6 items-center justify-center">
          {iconLeft}
        </span>
      ) : null}
      <span className="inline-flex items-center justify-center">
        {children}
      </span>
    </button>
  );
}
